import { z } from 'zod';

import {
  listPlatformOperatorsResponseSchema,
  listPracticesResponseSchema,
  platformAuditLogResponseSchema,
  practiceResponseSchema,
  type ListPlatformOperatorsResponse,
  type ListPracticesResponse,
  type PlatformAuditLogResponse,
  type PracticeResponse,
  type Tier,
} from '@emr/shared';

import { apiRequest } from './client.ts';

/** `GET /api/platform/practices` — every practice, platform-wide. `platform:manage_practices`. */
export function fetchPractices(signal?: AbortSignal): Promise<ListPracticesResponse> {
  return apiRequest(
    '/api/platform/practices',
    listPracticesResponseSchema,
    signal === undefined ? {} : { signal },
  );
}

export function suspendPractice(orgId: string): Promise<PracticeResponse> {
  return apiRequest(`/api/platform/practices/${orgId}/suspend`, practiceResponseSchema, {
    method: 'POST',
  });
}

export function reactivatePractice(orgId: string): Promise<PracticeResponse> {
  return apiRequest(`/api/platform/practices/${orgId}/reactivate`, practiceResponseSchema, {
    method: 'POST',
  });
}

export function updatePracticeTier(orgId: string, tier: Tier): Promise<PracticeResponse> {
  return apiRequest(`/api/platform/practices/${orgId}/tier`, practiceResponseSchema, {
    method: 'POST',
    body: { tier },
  });
}

/** `GET /api/platform/operators` — every active platform-operator grant. `platform:manage_practices`. */
export function fetchPlatformOperators(
  signal?: AbortSignal,
): Promise<ListPlatformOperatorsResponse> {
  return apiRequest(
    '/api/platform/operators',
    listPlatformOperatorsResponseSchema,
    signal === undefined ? {} : { signal },
  );
}

/**
 * `POST /api/platform/operators/:userId/revoke` — 204 with no body on success. `z.unknown()`,
 * matching `api/auth.ts`'s `logout` precedent: nothing here reads the (empty) body. 409 on the
 * zero-superuser guard or an already-revoked grant surfaces as a thrown `ApiError`, which the
 * caller renders — it is not this function's job to interpret the error code.
 */
export function revokePlatformOperator(userId: string): Promise<unknown> {
  return apiRequest(`/api/platform/operators/${userId}/revoke`, z.unknown(), { method: 'POST' });
}

/** `GET /api/platform/audit-log` — the platform's own operational events. `platform:read_audit_log`. */
export function fetchPlatformAuditLog(signal?: AbortSignal): Promise<PlatformAuditLogResponse> {
  return apiRequest(
    '/api/platform/audit-log',
    platformAuditLogResponseSchema,
    signal === undefined ? {} : { signal },
  );
}
